import { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import {
	createCrossJoinFlowHandler,
	type CrossJoinFlowHandler,
} from '@atlassian/loom-utils/createCrossJoinFlowHandler';

import { defaultRegistry } from 'react-sweet-state';

import { getLogger } from '@confluence/logger';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useLoomCrossJoinUrl } from './useLoomCrossJoinUrl.ts';
import {
	store as loomEntryPointGlobalStateStore,
	useLoomEntryPointGlobalStateActions,
} from './useLoomEntryPointGlobalState.ts';
import { useLoomEntryPointVariantActions } from './useLoomEntryPointVariantState.tsx';

const logger = getLogger('loom-cross-join-flow');

export type UseLoomCrossJoinFlowOptions = {
	entryPointLocation: string;
	onError?: (error: Error) => void;
	onPermissionGranted?: () => void;
	onPermissionUnchanged?: () => void;
};

export type UseLoomCrossJoinFlowReturn = {
	crossJoinUrl: string;
	openCrossJoinFlow: () => void;
};

/**
 * React hook wrapping the shared `createCrossJoinFlowHandler`.
 *
 * Connects to Confluence's Sweet State store so that when the cross-join flow
 * grants Loom permission, the SDK is automatically re-initialised and the user
 * can begin recording without a page reload.
 *
 * Returns `{ crossJoinUrl, openCrossJoinFlow }`.
 * Handles React lifecycle cleanup automatically.
 */
export const useLoomCrossJoinFlow = ({
	entryPointLocation,
	onError,
	onPermissionGranted,
	onPermissionUnchanged,
}: UseLoomCrossJoinFlowOptions): UseLoomCrossJoinFlowReturn => {
	const { cloudId, environment } = useSessionData();
	const crossJoinUrl = useLoomCrossJoinUrl();
	const { initLoomSDK } = useLoomEntryPointGlobalStateActions();
	const { setCrossJoinSuccess } = useLoomEntryPointVariantActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Keep callbacks in refs so the handler always calls the latest version
	// without needing to be recreated.
	const onErrorRef = useRef(onError);
	const onPermissionGrantedRef = useRef(onPermissionGranted);
	const onPermissionUnchangedRef = useRef(onPermissionUnchanged);
	const createAnalyticsEventRef = useRef(createAnalyticsEvent);

	useEffect(() => {
		onErrorRef.current = onError;
	}, [onError]);

	useEffect(() => {
		onPermissionGrantedRef.current = onPermissionGranted;
	}, [onPermissionGranted]);

	useEffect(() => {
		onPermissionUnchangedRef.current = onPermissionUnchanged;
	}, [onPermissionUnchanged]);

	useEffect(() => {
		createAnalyticsEventRef.current = createAnalyticsEvent;
	}, [createAnalyticsEvent]);

	const handlerRef = useRef<CrossJoinFlowHandler | null>(null);

	// Create the handler once (and recreate if key dependencies change).
	useEffect(() => {
		logger.debug`Creating cross-join flow handler for cloudId=${cloudId}, entryPoint=${entryPointLocation}`;

		const fireAnalyticsEvent = (
			type: 'sendOperationalEvent' | 'sendTrackEvent',
			action: string,
			actionSubject: string,
			attributes?: Record<string, unknown>,
		) => {
			createAnalyticsEventRef
				.current({
					type,
					data: {
						action,
						actionSubject,
						source: entryPointLocation,
						attributes: {
							entrypoint: entryPointLocation,
							...attributes,
						},
					},
				})
				.fire();
		};

		const handler = createCrossJoinFlowHandler({
			cloudId,
			crossJoinUrl,
			onListenerRegistered: () => {
				fireAnalyticsEvent('sendOperationalEvent', 'registered', 'loomCrossJoinListener');
			},
			onListenerTriggered: () => {
				fireAnalyticsEvent('sendOperationalEvent', 'triggered', 'loomCrossJoinListener');
			},
			onListenerTimedOut: () => {
				fireAnalyticsEvent('sendOperationalEvent', 'timedOut', 'loomCrossJoinListener');
			},
			onPermissionGranted: async () => {
				logger.info`Permission granted — updating variant to CO_USE and re-initialising SDK`;
				onPermissionGrantedRef.current?.();

				setCrossJoinSuccess();
				fireAnalyticsEvent('sendTrackEvent', 'transitioned', 'loomCrossJoinVariant', {
					adType: 'crossJoin',
				});

				await initLoomSDK({ cloudId, environment, entryPointLocation });

				const { initializedLoomSDK, initializedLoomSDKError } = defaultRegistry
					.getStore(loomEntryPointGlobalStateStore)
					.storeState.getState();

				if (initializedLoomSDK) {
					fireAnalyticsEvent('sendTrackEvent', 'reinitialised', 'loomCrossJoinSdk', {
						adType: 'crossJoin',
					});
					fireAnalyticsEvent('sendTrackEvent', 'completed', 'loomCrossJoinFlow', {
						adType: 'crossJoin',
					});
				} else if (initializedLoomSDKError) {
					fireAnalyticsEvent('sendOperationalEvent', 'reinitialisationFailed', 'loomCrossJoinSdk', {
						adType: 'crossJoin',
						error: initializedLoomSDKError.message,
					});
				}
			},
			onPermissionUnchanged: () => {
				logger.debug`Permission unchanged after cross-join flow`;
				onPermissionUnchangedRef.current?.();
			},
			onError: (error: Error) => {
				logger.warn`Cross-join flow error: ${error.message}`;
				onErrorRef.current?.(error);
			},
		});

		handlerRef.current = handler;

		return () => {
			logger.debug`Cleaning up cross-join flow handler`;
			handler.cleanup();
			handlerRef.current = null;
		};
	}, [cloudId, crossJoinUrl, environment, entryPointLocation, initLoomSDK, setCrossJoinSuccess]);

	const openCrossJoinFlow = useCallback(() => {
		logger.debug`Opening cross-join flow`;
		handlerRef.current?.open();
	}, []);

	return { crossJoinUrl, openCrossJoinFlow };
};
